import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { fetchOrgPeople } from '../../store/actions/OnboardingActions';
import apis from '../../utils/apis';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import DateTimePicker from '../shared/TailwindComponents/DatePicker';
import CurrencyPicker from '../shared/TailwindComponents/CurrencyPicker';
import Button from '../shared/TailwindComponents/Button';
import { ymdToDate, dateToYmd } from '../Matters/economics/dateHelpers';
import type { FirmEconomicsFilters } from '../../types/economics';
import { toCategoryOptions } from '../Matters/matterCategories';

interface FirmEconomicsFilterBarProps {
  value: FirmEconomicsFilters;
  onChange: (next: FirmEconomicsFilters) => void;
}

/** The compact field height every filter here shares (the list-page filter-bar height). */
const FIELD = 'h-8 py-0';

interface Person { user_id: string; first_name: string; last_name: string; email: string; }

/** Filter bar for the firm economics dashboard (US #2239, task #2279). */
const FirmEconomicsFilterBar: React.FC<FirmEconomicsFilterBarProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const people = useSelector((state: RootState) => (state.orgPeople?.people as Person[]) || []);

  const [clientOptions, setClientOptions] = useState<DropdownOption[]>([]);
  const [matterTypeOptions, setMatterTypeOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    dispatch(fetchOrgPeople());
    (async () => {
      try {
        const res = await apis.get('/clients', { params: { page: '1', page_size: '100' } });
        const clients = res?.data?.clients || [];
        setClientOptions(clients.map((c: any) => ({
          value: String(c.uuid || c.id || c.ClientID || ''),
          label: String(c.Name || c.name || 'Unknown'),
        })));
      } catch { /* leave empty */ }
      try {
        const res = await apis.get('/dropdown/matter-categories', { params: { active_only: true } });
        const cats = res?.data?.data || [];
        setMatterTypeOptions(toCategoryOptions(cats, t));
      } catch { /* leave empty */ }
    })();
  }, [dispatch]);

  const peopleOptions: DropdownOption[] = people.map((p) => ({
    value: String(p.user_id),
    label: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email,
  }));

  const set = (patch: Partial<FirmEconomicsFilters>) => onChange({ ...value, ...patch });
  const asArray = (v: string | string[]) => (Array.isArray(v) ? v : v ? [v] : []);

  const hasAnyFilter =
    !!value.date_from || !!value.date_to || !!value.currency ||
    !!value.client_id?.length || !!value.matter_type_id?.length || !!value.user_id?.length;

  return (
    /*
      ONE compact row that spans the page edge to edge, every filter the same
      width (R4-12): `lg:flex` with each control `flex-1 min-w-0` (FieldShell's
      own wrapper), 2 / 3 per row on narrower screens. No labels - each
      control says what it filters in its placeholder, like the list-page
      filter bars - and h-8 fields, the height of those bars' controls.
      Clear filters keeps its slot so the widths don't jump when it appears.
    */
    <div className="surface-canvas grid grid-cols-2 sm:grid-cols-3 lg:flex lg:items-center gap-2">
      <DateTimePicker
        id="firm-from"
        selected={ymdToDate(value.date_from)}
        placeholder={t('From')}
        showTimeSelect={false}
        dateFormat="yyyy-MM-dd"
        fullWidth
        className={FIELD}
        onChange={(d) => set({ date_from: dateToYmd(d) || undefined })}
      />
      <DateTimePicker
        id="firm-to"
        selected={ymdToDate(value.date_to)}
        placeholder={t('To')}
        showTimeSelect={false}
        dateFormat="yyyy-MM-dd"
        fullWidth
        className={FIELD}
        onChange={(d) => set({ date_to: dateToYmd(d) || undefined })}
      />
      <CurrencyPicker
        id="firm-currency"
        value={value.currency}
        placeholder={t('All currencies')}
        onChange={(currency) => set({ currency: currency || undefined })}
        compact
        fullWidth
        className={FIELD}
      />
      <Dropdown
        id="firm-clients"
        multiple
        options={clientOptions}
        value={(value.client_id || []).map(String)}
        placeholder={t('All clients')}
        onChange={(v) => set({ client_id: asArray(v) })}
        compact
        fullWidth
        className={FIELD}
      />
      <Dropdown
        id="firm-matter-types"
        multiple
        options={matterTypeOptions}
        value={(value.matter_type_id || []).map(String)}
        placeholder={t('All types')}
        onChange={(v) => set({ matter_type_id: asArray(v) })}
        compact
        fullWidth
        className={FIELD}
      />
      <Dropdown
        id="firm-users"
        multiple
        options={peopleOptions}
        value={(value.user_id || []).map(String)}
        placeholder={t('All users')}
        onChange={(v) => set({ user_id: asArray(v) })}
        compact
        fullWidth
        className={FIELD}
      />
      <div className="col-span-full flex justify-end lg:col-span-1 lg:shrink-0">
        <Button
          variant="neutral"
          size="small"
          onClick={() => onChange({})}
          disabled={!hasAnyFilter}
          className={hasAnyFilter ? undefined : 'invisible lg:visible'}
        >
          {t('Clear filters')}
        </Button>
      </div>
    </div>
  );
};

export default FirmEconomicsFilterBar;
